// `window.gtag` / `window.dataLayer` are declared in src/global.d.ts.
import type { AnyRouter } from '@tanstack/react-router'
import { CONSENT_CHANGE_EVENT, readConsent } from './cookie-consent'

export const gtag = (...args: unknown[]): void => {
  if (typeof window !== 'undefined') {
    if (typeof window.gtag === 'undefined') {
      window.dataLayer = window.dataLayer ?? []
      window.gtag = function () {
        window.dataLayer?.push(arguments)
      }
    }

    window.gtag(...args)
  }
}

// Google Analytics is a "statistics" cookie under our consent model, so it must not load or track
// until the visitor opts in. The loader script is injected lazily on consent (never before). Once
// loaded, GA's own collection is governed by Consent Mode (analytics_storage), synced on every consent
// change — so withdrawing consent mid-session flips it back to 'denied' and GA stops collecting/setting
// cookies, not just our manual page_view event. (Removing the script alone wouldn't stop the already
// configured library.)
let gaLoaded = false
let pageViewsBound = false

function loadGoogleAnalytics(measurementId: string): void {
  if (gaLoaded || typeof document === 'undefined') return
  gaLoaded = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  gtag('js', new Date())
  // Start denied so the initial config hit collects nothing until we explicitly grant in `apply`; a later
  // withdrawal flips it back to denied. Must be set BEFORE `config` to gate that first automatic page_view.
  gtag('consent', 'default', { analytics_storage: 'denied' })
  gtag('config', measurementId)
}

function syncAnalyticsConsent(granted: boolean): void {
  gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' })
}

function bindPageViews(router: AnyRouter): void {
  if (pageViewsBound) return
  pageViewsBound = true

  router.history.subscribe((update: { location: { href: string } }) => {
    // Re-check on every navigation so consent withdrawn after load stops sending events.
    if (readConsent().statistics) {
      gtag('event', 'page_view', { page_location: update.location.href })
    }
  })
}

/**
 * Wire up analytics with consent gating. Safe to call once at startup: it loads + configures GA only
 * when statistics consent is present, and re-checks whenever the visitor changes their choice.
 */
export function setupAnalytics(router: AnyRouter): void {
  if (typeof window === 'undefined' || !import.meta.env.PROD) return

  // Measurement ID comes from the GA_MEASUREMENT_ID env var (injected onto window SSR-side);
  // falls back to the historical hardcoded ID when the env var isn't set.
  const measurementId = window.GA_MEASUREMENT_ID || 'G-8S9DQNJQP9'

  const apply = (): void => {
    const granted = readConsent().statistics
    if (granted) {
      loadGoogleAnalytics(measurementId)
      bindPageViews(router)
    }
    // Once GA is loaded this session, keep Consent Mode in sync on every change — a withdrawal after load
    // sets analytics_storage back to 'denied' so GA stops collecting. No-op before GA has ever loaded.
    if (gaLoaded) syncAnalyticsConsent(granted)
  }

  apply()
  window.addEventListener(CONSENT_CHANGE_EVENT, apply)
}
